import type { ReactNode } from "react";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils";

type ScoreTone = "good" | "fair" | "poor" | "muted";

function scoreTone(score: number): ScoreTone {
  if (!Number.isFinite(score) || score <= 0) return "muted";
  if (score >= 4) return "good";
  if (score >= 3) return "fair";
  return "poor";
}

const scoreValueVariants = cva("text-[0.8125rem] font-semibold tabular-nums", {
  variants: {
    tone: {
      good: "text-emerald-600 dark:text-emerald-400",
      fair: "text-amber-600 dark:text-amber-400",
      poor: "text-rose-600 dark:text-rose-400",
      muted: "text-muted-foreground",
    },
  },
});

const scoreSegmentVariants = cva("h-[5px] w-4 rounded-[1px]", {
  variants: {
    tone: {
      good: "bg-emerald-600 dark:bg-emerald-400",
      fair: "bg-amber-600 dark:bg-amber-400",
      poor: "bg-rose-600 dark:bg-rose-400",
      muted: "bg-muted-foreground/40",
    },
    filled: {
      true: "",
      false: "bg-muted",
    },
  },
  compoundVariants: [
    { filled: false, tone: "good", className: "bg-muted" },
    { filled: false, tone: "fair", className: "bg-muted" },
    { filled: false, tone: "poor", className: "bg-muted" },
    { filled: false, tone: "muted", className: "bg-muted" },
  ],
});

const scoreBadgeVariants = cva(
  "inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-semibold tabular-nums ring-1 ring-inset",
  {
    variants: {
      tone: {
        good:
          "bg-emerald-500/10 text-emerald-700 ring-emerald-500/30 dark:text-emerald-300",
        fair: "bg-amber-500/10 text-amber-700 ring-amber-500/30 dark:text-amber-300",
        poor: "bg-rose-500/10 text-rose-700 ring-rose-500/30 dark:text-rose-300",
        muted: "bg-muted/60 text-foreground ring-border",
      },
    },
  }
);

/**
 * Score card for Story Craft / Editor scoring.
 *
 * Deliberately neutral: the surface stays `bg-card` and status colour appears
 * only on the numeral and the meter. Tinting the whole card by score made every
 * panel read as a stack of alerts.
 *
 * The meter is discrete because a 1–5 score is discrete; a continuous bar under
 * the label also read as a text underline.
 */
export function ScoreCard({
  label,
  score,
  max = 5,
  notes,
  footer,
  className,
}: {
  label: string;
  score: number;
  max?: number;
  notes?: ReactNode;
  footer?: ReactNode;
  className?: string;
}) {
  const tone = scoreTone(score);
  const valid = Number.isFinite(score) && score > 0;
  const filled = valid ? Math.round(score) : 0;

  return (
    <section
      data-slot="score-card"
      className={cn(
        "flex flex-col rounded-md border border-border/70 bg-card px-4 pt-3.5 pb-4",
        className
      )}
    >
      <div className="mb-2 flex items-center justify-between gap-4">
        <h4 className="m-0 text-[0.8125rem] font-semibold leading-snug text-foreground">
          {label}
        </h4>
        <div
          className="flex shrink-0 items-center gap-3"
          role="img"
          aria-label={valid ? `${score} out of ${max}` : "Not scored"}
        >
          <div className="flex gap-0.5" aria-hidden>
            {Array.from({ length: max }, (_, i) => (
              <span
                key={i}
                className={scoreSegmentVariants({ tone, filled: i < filled })}
              />
            ))}
          </div>
          <span className={scoreValueVariants({ tone })}>
            {valid ? `${score}/${max}` : "—"}
          </span>
        </div>
      </div>
      {notes ? (
        <div className="max-w-[68ch] text-sm leading-relaxed text-foreground/80">{notes}</div>
      ) : null}
      {footer ? <div className="mt-2.5">{footer}</div> : null}
    </section>
  );
}

export function ScoreBadge({
  score,
  label,
  className,
}: {
  score: number;
  label?: string;
  className?: string;
}) {
  const tone = scoreTone(score);
  return (
    <span
      data-slot="score-badge"
      className={cn(scoreBadgeVariants({ tone }), className)}
    >
      {score.toFixed(1)}
      {label ? <span className="font-medium opacity-75">{label}</span> : null}
    </span>
  );
}
